import React, { useState, useEffect, useRef } from "react";
import { X, Plus } from "lucide-react";

/** Generic input renderer used by <FixedEditor> and item forms. */
export function FieldInput({ field, value, onChange }) {
  const setValue = (v) => onChange(v);
  const common = "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-[#12964A] focus:outline-none transition-colors";

  switch (field.type) {
    case "longtext":
    case "paragraphs":
    case "markdown": {
      // Paragraphs stored as array of strings — join/split with \n\n
      const isArray = field.type === "paragraphs" && Array.isArray(value);
      const display = isArray ? value.join("\n\n") : (value ?? "");
      return (
        <textarea
          value={display}
          onChange={(e) => setValue(field.type === "paragraphs" ? e.target.value.split(/\n{2,}/).filter(Boolean) : e.target.value)}
          rows={field.rows || 3}
          maxLength={field.max}
          className={common}
          data-testid={`field-${field.key}`}
          placeholder={field.type === "paragraphs" ? "Separa paragrafi con riga vuota." : ""}
        />
      );
    }
    case "boolean":
      return (
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => setValue(e.target.checked)}
            className="rounded"
            data-testid={`field-${field.key}`}
          />
          <span className="text-sm text-slate-700">{value ? "Attivo" : "Disattivo"}</span>
        </label>
      );
    case "number":
      return (
        <input
          type="number"
          value={value ?? ""}
          onChange={(e) => setValue(e.target.value === "" ? null : Number(e.target.value))}
          className={common}
          data-testid={`field-${field.key}`}
        />
      );
    case "url":
    case "image":
    case "text":
    case "template":
    default:
      return (
        <input
          type="text"
          value={value ?? ""}
          onChange={(e) => setValue(e.target.value)}
          maxLength={field.max}
          className={common}
          data-testid={`field-${field.key}`}
          placeholder={field.placeholders?.join(" ")}
        />
      );
    case "chips":
    case "highlight":
      return <ChipInput value={value} onChange={setValue} field={field} />;
  }
}

/** Chip input for arrays of strings (highlight terms, popular chips, bullets). */
export function ChipInput({ value, onChange, field }) {
  const [input, setInput] = useState("");
  const chips = Array.isArray(value) ? value : [];
  const add = () => {
    const v = input.trim();
    if (!v) return;
    if (field.max_items && chips.length >= field.max_items) return;
    onChange([...chips, v]);
    setInput("");
  };
  const remove = (i) => onChange(chips.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {chips.map((c, i) => (
          <span key={i} className="inline-flex items-center gap-1 bg-slate-100 rounded-full px-2.5 py-1 text-xs">
            {c}
            <button onClick={() => remove(i)} className="text-slate-400 hover:text-red-600" data-testid={`chip-remove-${field.key}-${i}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"
          placeholder="Aggiungi voce e premi Invio"
          data-testid={`chip-input-${field.key}`}
        />
        <button onClick={add} className="border border-slate-200 rounded-lg px-3 py-2 text-sm hover:border-slate-400" data-testid={`chip-add-${field.key}`}>
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
